"use client";

import { RefObject, useCallback, useLayoutEffect, useState } from "react";
import { getBoxToBoxArrow } from "perfect-arrows";

/**
 * Draws a curved pointer arrow from a source element (the modal card) to
 * the floating AINA button. Geometry is computed live so it adapts to any
 * viewport size. The floating button is anchored by fixed CSS offsets
 * (bottom / right), so its box is derived from the viewport.
 */
type IntroPointerArrowProps = {
  fromRef: RefObject<HTMLElement | null>;
  from?: string;
  to?: string;
  angle?: number;
  strokeWidth?: number;
  targetSize?: number;
  targetRight?: number;
  targetBottom?: number;
};

type Geometry = {
  w: number;
  h: number;
  path: string;
  ex: number;
  ey: number;
  endAngle: number;
};

const GRADIENT_ID = "aina-arrow-gradient";

export default function IntroPointerArrow({
  fromRef,
  from = "#6CD8A2",
  to = "#FFFFFF",
  angle = 106.27,
  strokeWidth = 3,
  targetSize = 76,
  // Matches .aina-wrapper: bottom: 40px, right: 30px in marketplace-ui2
  targetRight = 30,
  targetBottom = 40,
}: IntroPointerArrowProps) {
  const [geo, setGeo] = useState<Geometry | null>(null);

  const compute = useCallback(() => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const card = fromRef.current?.getBoundingClientRect();
    if (!card) return;

    const bx = w - targetRight - targetSize;
    const by = h - targetBottom - targetSize;
    const bCx = bx + targetSize / 2;
    const bCy = by + targetSize / 2;

    const [sx, sy, cx, cy, ex, ey, ae] = getBoxToBoxArrow(
      card.left,
      card.top,
      card.width,
      card.height,
      bCx,
      bCy,
      0,
      0,
      { bow: 0.2, stretch: 0.4, padStart: 4, padEnd: targetSize / 2 + 6, flip: false },
    );

    setGeo({
      w,
      h,
      ex,
      ey,
      endAngle: ae * (180 / Math.PI),
      path: `M ${sx} ${sy} Q ${cx} ${cy} ${ex} ${ey}`,
    });
  }, [fromRef, targetSize, targetRight, targetBottom]);

  useLayoutEffect(() => {
    compute();

    const raf = requestAnimationFrame(compute);
    window.addEventListener("resize", compute);

    let ro: ResizeObserver | undefined;
    if (fromRef.current && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(compute);
      ro.observe(fromRef.current);
    }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", compute);
      ro?.disconnect();
    };
  }, [compute, fromRef]);

  if (!geo) return null;

  const rad = (angle * Math.PI) / 180;
  const dx = Math.sin(rad);
  const dy = -Math.cos(rad);
  const g = {
    x1: 0.5 - dx * 0.5,
    y1: 0.5 - dy * 0.5,
    x2: 0.5 + dx * 0.5,
    y2: 0.5 + dy * 0.5,
  };

  return (
    <svg
      className="aina-intro-modal__pointer"
      width={geo.w}
      height={geo.h}
      viewBox={`0 0 ${geo.w} ${geo.h}`}
      fill="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient
          id={GRADIENT_ID}
          x1={g.x1}
          y1={g.y1}
          x2={g.x2}
          y2={g.y2}
        >
          {/* stopColor set via style so CSS variables (e.g. the client brand
              token passed in `from`) resolve reliably across browsers. */}
          <stop offset="0.0121" style={{ stopColor: from }} />
          <stop offset="1" style={{ stopColor: to }} />
        </linearGradient>
      </defs>

      <path
        d={geo.path}
        stroke={`url(#${GRADIENT_ID})`}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />

      <path
        d="M -11 -7 L 0 0 L -11 7"
        fill="none"
        stroke={to}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        transform={`translate(${geo.ex} ${geo.ey}) rotate(${geo.endAngle})`}
      />
    </svg>
  );
}
